<script setup lang="ts">
import indexContent from "~/content/index.json";

const { staticUrl } = useImageUrl();
const makersStore = useMakersStore();
const reviewsStore = useReviewsStore();
const uiStore = useUiStore();

definePageMeta({
  layout: "default",
});

const config = useRuntimeConfig();
const siteOrigin = config.public.mainUrl as string;

useSeoMeta({
  title: "Office Catering London | London's Best Workplace Catering",
  description:
    "Office catering in London, done right. Quality food made by independent Makers and delivered directly to your team.",
  ogTitle: "Office Catering London | London's Best Workplace Catering",
  ogDescription: indexContent.hero.title,
  ogType: "website",
});

useHead({
  link: [{ rel: "canonical", href: siteOrigin }],
});

// One @graph: Organization + FAQPage. FAQ answers must also be in the DOM
// (see template) — Google will not grant FAQPage rich results for schema alone.
// Built from the SAME content array the accordion renders, so markup and schema
// cannot drift apart.
useSchemaGraph("ld-home", [
  {
    "@type": "Organization",
    // Stable @id so other pages' nodes (FoodEstablishment.publisher, the
    // WebSite node, BreadcrumbList) can reference this one instead of every
    // page carrying a disconnected copy.
    "@id": `${siteOrigin}/#organization`,
    name: "&Dine",
    url: siteOrigin,
    // `logo` deliberately omitted: /img/logo.svg does not exist (public/ has no
    // img/ directory and no logo asset at all), so emitting it would publish a
    // 404 as the brand mark. Restore once a real asset is in place —
    // see specs/aeo-knowledge-graph.md §4.1.6.
    description:
      "Office catering in London, done right. Quality food made by independent Makers and delivered directly to your team.",
    foundingDate: "2021",
    areaServed: { "@type": "City", name: "London", addressCountry: "GB" },
    address: {
      "@type": "PostalAddress",
      streetAddress: "Beyond, Fox Court, 14 Gray's Inn Road",
      addressLocality: "London",
      postalCode: "WC1X 8HN",
      addressCountry: "GB",
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: "hello@anddine.co.uk",
      contactType: "customer service",
    },
    // These are the profiles the site itself links to in the footer, plus
    // LinkedIn. `sameAs` is how an answer engine ties the brand to its verified
    // profiles, so a wrong handle here attaches &Dine to someone else's account.
    //
    // The Instagram handle was `/anddine/` — confirmed wrong; the brand is
    // `anddinecooking`, matching the footer. An HTTP probe can't tell these
    // apart (Instagram and X both return 200 behind a login wall for any
    // handle), so this is confirmed by the owner, not inferred.
    sameAs: [
      "https://www.instagram.com/anddinecooking/",
      "https://www.facebook.com/anddinecooking",
      "https://twitter.com/anddinecooking",
      "https://www.linkedin.com/company/anddine/",
    ],
  },
  {
    "@type": "FAQPage",
    "@id": `${siteOrigin}/#faq`,
    mainEntity: indexContent.faqs.items.map((item: { title: string; answer: string }) => ({
      "@type": "Question",
      name: item.title,
      acceptedAnswer: { "@type": "Answer", text: item.answer.trim() },
    })),
  },
]);

const showDownloadForm = ref(false);
const faqIndex = ref<number | null>(null);
const isMobile = ref(false);

function openLandingPage(url: string) {
  window.open(url, "_blank");
}

function initTrustpilot() {
  if (typeof window === "undefined") return;
  const delays = [300, 600, 1000, 2000];
  delays.forEach((delay, index) => {
    setTimeout(() => {
      const widgets = document.querySelectorAll(".trustpilot-widget");
      if (widgets.length > 0 && (window as any).Trustpilot) {
        widgets.forEach((widget) => {
          try {
            (window as any).Trustpilot.loadFromElement(widget);
          } catch {
            // Silent fail
          }
        });
      } else if (index === delays.length - 1) {
        if (!document.getElementById("trustpilot-script") && widgets.length > 0) {
          const script = document.createElement("script");
          script.id = "trustpilot-script";
          script.type = "text/javascript";
          script.src = "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
          script.async = true;
          script.defer = true;
          script.onload = () => {
            if ((window as any).Trustpilot) {
              widgets.forEach((widget) => {
                try {
                  (window as any).Trustpilot.loadFromElement(widget);
                } catch {
                  // Silent fail
                }
              });
            }
          };
          document.body.appendChild(script);
        }
      }
    }, delay);
  });
}

onMounted(async () => {
  isMobile.value = window.innerWidth <= 768;
  window.addEventListener("resize", () => {
    isMobile.value = window.innerWidth <= 768;
  });
  uiStore.setPageLoading(true);
  await Promise.all([
    makersStore.getMakerCategory("customer favourites"),
    reviewsStore.getReviews(),
  ]);
  uiStore.setPageLoading(false);
  nextTick(() => {
    initTrustpilot();
  });
});
</script>

<template>
  <section class="info">
    <!-- Services Overlay -->
    <div
      v-if="uiStore.showServices"
      id="services-menu"
      class="info__services"
    >
      <fa
        class="info__services--cross"
        :icon="['fal', 'xmark']"
        @click="uiStore.setShowServices(false)"
      />
      <div
        v-for="(item, i) in indexContent.services.items"
        :key="`item_${i}`"
        class="info__services--service"
        @click="item.external ? openLandingPage(item.url) : navigateTo(item.url)"
      >
        <p class="grey-text">{{ item.title }}</p>
        <p class="light-grey-text small-text">{{ item.content }}</p>
      </div>
    </div>

    <!-- Hero Section -->
    <div class="info__hero">
      <div class="info__hero--top">
        <h1 class="light-grey-text large-text">{{ indexContent.hero.h1 }}</h1>
        <h2 class="grey-text title">{{ indexContent.hero.title }}</h2>
        <ClientOnly>
          <div
            class="trustpilot-widget"
            data-locale="en-GB"
            data-template-id="53aa8807dec7e10d38f59f32"
            data-businessunit-id="61ded0c7cd06a8e241139895"
            data-style-width="80%"
            data-theme="light"
          >
            <a
              href="https://uk.trustpilot.com/review/anddine.co.uk"
              target="_blank"
              rel="noopener"
            ></a>
          </div>
        </ClientOnly>
        <p class="grey-text subtitle">{{ indexContent.hero.body }}</p>
        <div class="testimonials">
          <h4 class="grey-text title">{{ indexContent.testimonials?.title }}</h4>
          <img
            class="image"
            loading="eager"
            decoding="async"
            :src="staticUrl('/Images/gifs/logos.gif')"
            alt="Trusted by leading companies for office catering"
          />
        </div>
        <div class="wrapper">
          <DynamicButton
            :text="indexContent.hero.order_button"
            color="pink"
            width="50%"
            @click-event="navigateTo('/contact')"
          />
        </div>
      </div>
      <img
        class="info__hero--image"
        :src="staticUrl(`/Images/biz/${indexContent.hero.image}`)"
        :alt="indexContent.hero.title || 'And Dine office catering London'"
        loading="eager"
        fetchpriority="high"
        decoding="async"
        width="800"
        height="560"
      />
    </div>

    <!-- Offerings Section -->
    <div class="info__offerings">
      <h2 class="grey-text">{{ indexContent.offerings.title }}</h2>
      <p class="subtitle grey-text">{{ indexContent.offerings.subtitle }}</p>
      <div class="info__offerings--top">
        <div
          v-for="(item, i) in indexContent.offerings.items"
          :key="`offering_${i}`"
          class="slide"
        >
          <div class="slide__wrapper">
            <div
              class="slide__wrapper--photo"
              :style="`background-image: url('${staticUrl(`/Images/biz/${item.photo}.jpg`)}')`"
            ></div>
            <img
              loading="lazy"
              decoding="async"
              :src="staticUrl(`/Images/logos/${item.image}.png`)"
              :alt="item.image"
              width="200"
            />
            <p class="subtitle grey-text small-text">{{ item.subtitle }}</p>
            <ul class="slide__wrapper--list">
              <li
                v-for="(bullet, index) in item.list"
                :key="`bullet_${index}`"
              >
                <div>
                  <img
                    loading="lazy"
                    decoding="async"
                    :src="staticUrl('/Images/icons/tick.png')"
                    alt="tick"
                    width="30"
                  />
                  <p class="grey-text">{{ bullet.title }}</p>
                </div>
                <p class="small-text grey-text">{{ bullet.subtitle }}</p>
              </li>
            </ul>
            <DynamicButton
              :text="item.button"
              color="pink"
              width="80%"
              @click-event="item.internal ? navigateTo(item.url) : openLandingPage(item.url)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Values / Best Sellers -->
    <div class="info__values">
      <h2 class="grey-text">{{ indexContent.values.title }}</h2>
      <p class="grey-text">{{ indexContent.values.subtitle }}</p>
      <div class="info__values--wrapper">
        <div
          v-for="(item, i) in indexContent.values.items"
          :key="`value_${i}`"
          class="slide"
        >
          <div class="image">
            <img
              loading="lazy"
              decoding="async"
              :src="staticUrl(`/Images/biz/${item.image}`)"
              :alt="item.image"
            />
          </div>
          <h4 class="grey-text">{{ item.title }}</h4>
          <p class="grey-text">{{ item.subtitle }}</p>
          <DynamicButton
            :text="`View ${item.title}`"
            color="pink"
            width="80%"
            @click-event="navigateTo(`/store/${item.slug}`)"
          />
        </div>
      </div>
    </div>

    <!-- Reasons Section -->
    <div class="info__reasons">
      <div class="info__reasons--top">
        <div class="logo">
          <h2 class="grey-text">{{ indexContent.reasons.title_1 }}</h2>
        </div>
        <p class="subtitle grey-text">{{ indexContent.reasons.subtitle }}</p>
        <ClientOnly>
          <CarouselBase
            v-if="indexContent.reasons.list"
            :breakpoints="[
              [300, 1],
              [768, 2],
              [1100, 4],
            ]"
            :autoplay="true"
            :autoplay-interval="6000"
          >
            <CarouselSlide
              v-for="(item, i) in indexContent.reasons.list"
              :key="`reason_${i}`"
              :width="`${100 / 4}%`"
            >
              <div class="slide">
                <div class="slide__wrapper">
                  <img
                    loading="lazy"
                    decoding="async"
                    class="slide__wrapper--image"
                    :src="staticUrl(`/Images/biz/${item.image}`)"
                    :alt="item.image"
                  />
                  <div class="slide__wrapper--text">
                    <h4 class="grey-text">{{ item.title }}</h4>
                    <p class="grey-text">{{ item.body }}</p>
                  </div>
                </div>
              </div>
            </CarouselSlide>
          </CarouselBase>
        </ClientOnly>
        <DynamicButton
          class="button"
          :text="indexContent.reasons.button"
          color="pink"
          width="50%"
          @click-event="navigateTo('/contact')"
        />
      </div>
    </div>

    <!-- Trending Makers -->
    <div
      v-if="makersStore.chosenMakerCategory"
      class="info__trending"
    >
      <div class="info__trending--section">
        <h2 class="grey-text">
          {{ (makersStore.chosenMakerCategory as any).name }}
        </h2>
        <p
          class="grey-text"
          v-html="(makersStore.chosenMakerCategory as any).description"
        ></p>
        <ClientOnly>
          <CarouselBase
            :items-to-show="isMobile ? 1 : 4"
            :fade-edges="true"
          >
            <CarouselSlide
              v-for="maker in (makersStore.chosenMakerCategory as any).makers"
              :key="maker.slug"
              :width="`${100 / 4}%`"
            >
              <MakerList
                :maker="maker"
                :is-lazy="true"
              />
            </CarouselSlide>
          </CarouselBase>
        </ClientOnly>
      </div>
      <DynamicButton
        class="info__trending--button"
        :text="indexContent.hero.order_button"
        color="pink"
        @click-event="navigateTo('/contact')"
      />
    </div>

    <!-- Examples Section -->
    <div class="info__examples">
      <div class="info__examples--top">
        <h2 class="grey-text title">{{ indexContent.examples.title }}</h2>
        <p class="grey-text title">{{ indexContent.examples.subtitle }}</p>
        <ClientOnly>
          <CarouselBase
            v-if="indexContent.examples.list"
            :breakpoints="[
              [300, 1],
              [768, 2],
              [1100, 4],
            ]"
            :autoplay="true"
            :autoplay-interval="6000"
          >
            <CarouselSlide
              v-for="(example, index) in indexContent.examples.list"
              :key="`example_${index}`"
              :width="`${100 / 4}%`"
            >
              <div class="slide">
                <div class="slide__wrapper">
                  <img
                    loading="lazy"
                    decoding="async"
                    class="slide__wrapper--image"
                    :src="staticUrl(`/Images/biz/${example.image}.jpg`)"
                    :alt="example.image"
                  />
                  <div class="slide__wrapper--text">
                    <h4 class="grey-text">{{ example.title }}</h4>
                    <p class="grey-text">{{ example.description }}</p>
                  </div>
                </div>
              </div>
            </CarouselSlide>
          </CarouselBase>
        </ClientOnly>
      </div>
    </div>

    <!-- Instructions / Steps -->
    <!-- <div class="info__instructions">
      <div class="info__instructions--top">
        <HomeStepsBox />
      </div>
    </div> -->

    <!-- Download Section -->
    <div class="info__download">
      <img
        class="info__download--image"
        :src="staticUrl(`/Images/biz/${indexContent.download.image}.jpg`)"
        :alt="indexContent.download.image"
        loading="lazy"
        decoding="async"
      />
      <div class="info__download--wrapper">
        <h2 class="text grey-text">{{ indexContent.download.title }}</h2>
        <p class="text grey-text">{{ indexContent.download.body }}</p>
        <DynamicButton
          class="button"
          :text="indexContent.download.button"
          color="pink"
          @click-event="showDownloadForm = true"
        />
      </div>
    </div>

    <!-- Reviews Section -->
    <div class="info__reviews">
      <div class="info__reviews--top">
        <h2 class="title grey-text">{{ indexContent.reviews.title }}</h2>
        <p class="subtitle grey-text">{{ indexContent.reviews.subtitle }}</p>
        <ClientOnly>
          <div
            class="trustpilot-widget"
            data-locale="en-GB"
            data-template-id="5419b637fa0340045cd0c936"
            data-businessunit-id="61ded0c7cd06a8e241139895"
            data-style-height="20px"
            data-style-width="100%"
          >
            <a
              href="https://uk.trustpilot.com/review/anddine.co.uk"
              target="_blank"
              rel="noopener"
            ></a>
          </div>
        </ClientOnly>
        <ClientOnly>
          <CarouselBase
            v-if="indexContent.reviews.list"
            :breakpoints="[
              [300, 1],
              [768, 2],
              [1100, 3],
            ]"
            :gap="24"
            :autoplay="true"
            :autoplay-interval="6000"
            :navigation-enabled="false"
          >
            <CarouselSlide
              v-for="(review, index) in indexContent.reviews.list"
              :key="`review_${index}`"
              :width="`${100 / 3}%`"
            >
              <ReviewList :reviews="[review]" />
            </CarouselSlide>
          </CarouselBase>
        </ClientOnly>
      </div>
    </div>

    <!-- FAQs Section — answers stay in the DOM (CSS collapse, not v-if) so
         crawlers and FAQPage rich results can read them without JS. -->
    <div class="info__faqs">
      <h2 class="grey-text info__faqs--title">{{ indexContent.faqs.title }}</h2>
      <p class="grey-text info__faqs--subtitle">{{ indexContent.faqs.subtitle }}</p>
      <!-- Answers are collapsed with CSS, never `v-if`. Previously the answer <p>
           only existed while its item was open, so the server-rendered HTML carried
           the 7 questions and none of the answers — and Google will not grant the
           FAQPage rich result for content that isn't present and visible on the
           page. The accordion behaviour is unchanged. -->
      <div
        v-for="(item, i) in indexContent.faqs.items"
        :key="`faq_${i}`"
        class="info__faqs--drop"
      >
        <button
          type="button"
          class="grey-text info__faqs--trigger"
          :aria-expanded="faqIndex === i"
          :aria-controls="`faq_answer_${i}`"
          @click="faqIndex = faqIndex === i ? null : i"
        >
          <fa :icon="['fal', `chevron-${faqIndex === i ? 'up' : 'down'}`]" />
          {{ item.title }}
        </button>
        <div
          :id="`faq_answer_${i}`"
          class="info__faqs--answer"
          :class="{ 'info__faqs--answer-open': faqIndex === i }"
        >
          <p class="grey-text">{{ item.answer }}</p>
        </div>
      </div>
    </div>

    <!-- Download Modal -->
    <DynamicModal
      v-if="showDownloadForm"
      :full-screen="true"
      @close-modal="showDownloadForm = false"
    >
      <template #body>
        <h2 class="grey-text">{{ indexContent.download.title }}</h2>
        <p class="grey-text">{{ indexContent.download.body }}</p>
        <HubspotForm
          portal-id="139704907"
          form-id="64eb84d5-567a-49be-9d24-b2fbf58bdd65"
          region="eu1"
        />
      </template>
    </DynamicModal>
  </section>
</template>

<style lang="scss">
.info {
  display: flex;
  flex-direction: column;
  position: inherit;
  width: 100%;
  h2 {
    font-size: 2.4rem !important;
    line-height: 2.6rem !important;
  }
  &__services {
    position: fixed;
    display: flex;
    top: 5rem;
    width: 50%;
    left: calc(25% - 1.5rem);
    padding: 1.25rem;
    border-radius: 0 0 1rem 1rem;
    background-color: var(--color-white-1);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    z-index: 9999;
    gap: 0.75rem;
    @media (max-width: $tablet) {
      width: calc(100% - 2rem);
      flex-direction: column;
      left: 0;
    }
    &--cross {
      width: 4rem;
      margin-top: 1rem;
      cursor: pointer;
    }
    &--service {
      text-decoration: none;
      flex: 1;
      padding: 1.25rem;
      border-radius: 0.75rem;
      cursor: pointer;
      transition:
        background-color 0.2s ease,
        transform 0.2s ease;
      &:hover {
        background-color: var(--color-grey-4);
        transform: translateY(-2px);
      }
      @media (max-width: $tablet) {
        width: calc(90% - 2rem);
      }
    }
  }

  // -------- Hero --------
  &__hero {
    margin: 5rem 5% 0;
    width: 90%;
    color: var(--color-white-1);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 3rem;
    @media (max-width: $tablet) {
      margin: 80px 0 0;
      flex-direction: column-reverse;
      width: 100%;
      gap: 0;
    }
    &--image {
      border-radius: 1rem;
      width: 45%;
      margin: 1rem 0 4rem;
      height: 35rem;
      object-fit: cover;
      object-position: center;
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
      @media (max-width: $tablet) {
        margin: 0;
        width: 100%;
        border-radius: 0;
        height: 20rem;
        box-shadow: none;
      }
    }
    &--top {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin: 1rem 0 4rem;
      width: 50%;
      text-align: left;
      @media (max-width: $tablet) {
        width: 92%;
        text-align: center;
        margin: 2rem 4%;
        align-items: center;
      }
      h1 {
        font-size: 2.8rem;
        line-height: 3rem;
      }
      .wrapper {
        display: flex;
        width: 100%;
        margin-top: 1.5rem;
        @media (max-width: $tablet) {
          flex-direction: column;
          align-items: center;
          width: 96%;
          margin: 2rem 2%;
        }
      }
      .trustpilot-widget {
        margin-top: 1rem;
        cursor: pointer;
        z-index: 2222;
        height: 7rem;
        @media (max-width: $tablet) {
          height: auto;
          min-height: 5rem;
          margin: 1rem auto;
          width: 100%;
          display: flex;
          justify-content: center;
        }
      }
      .testimonials {
        display: flex;
        flex-direction: column;
        margin: 0 0 1rem;
        width: 100%;
        @media (max-width: $tablet) {
          width: 96%;
          text-align: center;
          margin: 2rem 2%;
        }
        .button,
        .title,
        .subtitle {
          margin: 0.5rem 0;
          display: flex;
          text-align: left;
          justify-content: flex-start;
          @media (max-width: $tablet) {
            text-align: center;
            justify-content: center;
          }
        }
        .title {
          margin: 1.5rem 0 1rem;
          @media (max-width: $tablet) {
            margin: 0.5rem 0;
          }
        }
        .image {
          width: 100%;
          @media (max-width: $tablet) {
            margin: 2rem 0 0;
          }
        }
      }
    }
  }

  // -------- Offerings --------
  &__offerings {
    text-align: center;
    margin: 0 auto !important;
    padding: 5rem 0;
    background-color: var(--color-grey-4);
    width: 100%;
    &--top {
      margin: 2.5rem 0 0;
      display: flex;
      justify-content: center;
      gap: 1.5rem;
      padding: 0 5%;
      @media (max-width: $tablet) {
        flex-direction: column;
        align-items: center;
        gap: 2rem;
      }
      .slide {
        flex: 1;
        max-width: 30%;
        @media (max-width: $tablet) {
          flex: 0 0 100%;
          max-width: 90%;
        }
        &__wrapper {
          width: 100%;
          border-radius: 1rem;
          background-color: var(--color-white-1);
          padding: 3rem 8% 2.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          box-sizing: border-box;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease;
          height: 100%;
          &:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
          }
          @media (max-width: $tablet) {
            width: 100%;
            padding: 2rem 5%;
          }
          &--photo {
            width: 100%;
            max-width: 14rem;
            aspect-ratio: 1;
            flex-shrink: 0;
            border-radius: 50%;
            background-size: cover;
            background-position: center;
            margin: 0 auto 1.5rem;
          }
          &--list {
            list-style: none;
            margin: 1.5rem 0;
            padding: 0;
            text-align: left;
            flex: 1;
            li {
              margin: 1.25rem 0;
              padding: 0;
              color: var(--color-pink-1);
              display: flex;
              align-items: flex-start;
              flex-direction: column;
              gap: 0.4rem;
              div {
                display: flex;
                align-items: center;
              }
              img {
                margin-right: 0.5rem;
              }
            }
          }
          .subtitle {
            text-align: left !important;
            width: 100%;
            margin-bottom: 0.5rem;
          }
        }
      }
    }
    .subtitle {
      width: 60%;
      margin: 0.5rem auto;
      line-height: 1.6;
      @media (max-width: $tablet) {
        width: 90%;
      }
    }
  }

  // -------- Values / Best Sellers --------
  &__values {
    background-color: var(--color-white-1);
    width: 100%;
    padding: 5rem 5% 3rem;
    text-align: center;
    box-sizing: border-box;
    &--wrapper {
      display: flex;
      justify-content: center;
      gap: 2rem;
      padding: 2.5rem 0;
      @media (max-width: $tablet) {
        flex-direction: column;
        align-items: center;
      }
      .slide {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        max-width: 20rem;
        text-align: center;
        @media (max-width: $tablet) {
          max-width: 100%;
          width: 80%;
          margin-bottom: 2rem;
        }
        .image {
          width: 16rem;
          height: 16rem;
          border-radius: 1rem;
          overflow: hidden;
          margin-bottom: 1rem;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
          @media (max-width: $laptop) {
            width: 14rem;
            height: 14rem;
          }
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.3s ease;
          }
          &:hover img {
            transform: scale(1.05);
          }
        }
        h4 {
          margin: 0.5rem 0 0.25rem;
        }
        .grey-text {
          flex: 1;
        }
      }
      .grey-text {
        line-height: 1.6;
        margin-bottom: 1rem;
      }
    }
  }

  // -------- Shared: Reasons, Examples, Reviews, Offerings, Instructions --------
  &__reasons {
    background-color: var(--color-grey-4);
    padding: 4rem 0 5rem;
    width: 100%;
    overflow: visible;
  }
  &__examples,
  &__reviews,
  &__reasons,
  &__offerings,
  &__instructions {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 2rem 0;
    &--top {
      padding: 0 5%;
      position: relative;
      .button,
      .title,
      .subtitle {
        margin: 0.5rem auto 1rem;
        display: flex;
        justify-content: center;
        text-align: center;
      }
      .title {
        margin: 1rem 0 0.5rem;
        @media (max-width: $tablet) {
          margin: 1rem 0;
        }
      }
      .logo {
        display: flex;
        justify-content: center;
        align-items: flex-end;
        img {
          width: 8rem;
          margin-bottom: 0.3rem;
        }
      }
      .slide {
        position: relative;
        margin: 0 auto;
        width: 100%;
        @media (max-width: $tablet) {
          display: inline;
          padding: 0;
          width: 90%;
        }
        &__wrapper {
          position: relative;
          width: 96%;
          height: 100%;
          border-radius: 1rem;
          margin: 0.5rem auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: left;
          background: var(--color-white-1);
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
          overflow: hidden;
          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease;
          &:hover {
            transform: translateY(-3px);
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
          }
          @media (max-width: $tablet) {
            display: flex;
            flex-direction: column;
          }
          &--image {
            border-radius: 1rem 1rem 0 0;
            width: 100%;
            aspect-ratio: 4 / 3;
            object-fit: cover;
            @media (max-width: $tablet) {
              width: 100%;
            }
          }
          &--text {
            padding: 1rem;
            width: 100%;
            box-sizing: border-box;
            @media (max-width: $tablet) {
              width: 100%;
            }
            h4 {
              text-align: left;
              margin-bottom: 0.25rem;
            }
            p {
              font-size: 0.6rem;
              line-height: 1.6;
            }
          }
        }
      }
    }
  }
  &__examples {
    @media (max-width: $tablet) {
      margin: 3rem 0 0 !important;
    }
    &--top {
      padding: 0 5%;
      position: relative;
      .title {
        justify-content: flex-start !important;
        margin-left: 0 !important;
      }
    }
  }

  // -------- Trending --------
  &__trending,
  &__examples {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 3rem 0 2rem;
    @media (max-width: $tablet) {
      margin: 1rem 0 0;
    }
    &--section,
    &--top {
      padding: 0 5%;
      position: relative;
      h2 {
        margin: 0 0 0.5rem 1%;
      }
      p.grey-text {
        margin: 0 0 1rem 1%;
        line-height: 1.6;
      }
    }
    &--button {
      display: flex;
      justify-content: center;
      margin: 1.5rem auto;
    }
    .button,
    .title,
    .subtitle {
      margin: 0.5rem auto;
      display: flex;
      justify-content: center;
    }
    .subtitle {
      margin: 0 auto 1.5rem;
    }
  }

  // -------- Reviews --------
  &__reviews {
    margin: 2rem 0 3rem !important;
    @media (max-width: $tablet) {
      margin: 2rem 0 0 !important;
    }
    .trustpilot-widget {
      margin-bottom: 2rem;
      cursor: pointer;
      z-index: 2222;
    }
  }

  // -------- Download --------
  &__download {
    display: flex;
    align-items: center;
    padding: 4rem 6%;
    justify-content: space-between;
    gap: 3rem;
    @media (max-width: $tablet) {
      flex-direction: column-reverse;
      margin: 1rem 0 2rem;
      padding: 2rem 5%;
      gap: 1.5rem;
    }
    &--wrapper {
      width: 45%;
      .text {
        margin-bottom: 1rem;
        line-height: 1.6;
      }
      @media (max-width: $tablet) {
        width: 100%;
        margin: 1rem 0;
      }
    }
    &--image {
      width: 45%;
      border-radius: 1rem;
      box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
      @media (max-width: $tablet) {
        width: 100%;
        margin: 1rem 0;
      }
    }
  }

  // -------- FAQs --------
  &__faqs {
    background-color: var(--color-grey-4);
    padding: 4rem 20%;
    @media (max-width: $laptop) {
      padding: 3rem 10%;
    }
    @media (max-width: $tablet) {
      padding: 2.5rem 4%;
    }
    &--title {
      text-align: center;
      margin-bottom: 0.5rem !important;
    }
    &--subtitle {
      text-align: center;
      margin: 0 auto 2rem;
      max-width: 36rem;
      line-height: 1.6;
    }
    &--drop {
      padding: 1.25rem 1.5rem;
      border-radius: 0.75rem;
      margin: 0.75rem 0;
      background-color: var(--color-white-1);
      box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
      cursor: pointer;
      transition: box-shadow 0.2s ease;
      &:hover {
        box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
      }
      svg {
        margin-right: 1rem;
        font-size: 0.85rem;
      }
    }
    // Real <button> (not h4[role=button]) so the accessibility tree stays
    // well-formed for assistive tech and agentic browsing audits.
    &--trigger {
      appearance: none;
      background: none;
      border: 0;
      padding: 0;
      margin: 0;
      width: 100%;
      font: inherit;
      color: inherit;
      text-align: left;
      cursor: pointer;
      display: flex;
      align-items: center;
    }
    // Keep every answer in the DOM; only expand visually when open. A `v-if` here
    // is what kept every answer out of the server-rendered markup, and out of
    // FAQPage rich-result eligibility.
    //
    // `grid-template-rows: 0fr → 1fr` animates to the content's own height. The
    // earlier max-height approach needed a magic 24rem ceiling, which silently
    // clips any answer longer than that.
    &--answer {
      display: grid;
      grid-template-rows: 0fr;
      overflow: hidden;
      opacity: 0;
      transition:
        grid-template-rows 0.25s ease,
        opacity 0.2s ease;

      > p {
        overflow: hidden;
        margin: 0;
        padding-left: 2rem;
        line-height: 1.65;
      }

      &-open {
        grid-template-rows: 1fr;
        opacity: 1;
        margin-top: 0.75rem;
      }
    }
  }

  // -------- Contact --------
  &__contact {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 2rem 6% 4rem;
    border: 2px solid var(--color-grey-3);
    border-radius: 1rem;
    padding: 3rem;
    width: calc(88% - 6rem);
    text-align: center;
    @media (max-width: $tablet) {
      margin: 2rem 5%;
      width: calc(90% - 6rem);
    }
    p {
      margin-bottom: 2rem;
    }
  }

  // -------- Mobile: centre every section header + intro paragraph --------
  // Section headers across `info__*` blocks were left-aligned by default
  // (some had `margin: 0 0 0.5rem 1%`, none had `text-align: center`).
  // On mobile that read as visually disjointed against the white cards
  // below which are centred. One blanket rule keeps it consistent.
  @media (max-width: $tablet) {
    &__reasons--top,
    &__trending--section,
    &__trending--top,
    &__examples--top,
    &__examples--section,
    &__reviews--top,
    &__offerings,
    &__values,
    &__download--wrapper {
      text-align: center;
      h1,
      h2,
      h3,
      h4 {
        margin-left: 0;
        margin-right: 0;
        text-align: center;
      }
      p {
        margin-left: 0;
        margin-right: 0;
        text-align: center;
      }
      .logo {
        justify-content: center;
        h2 {
          width: 100%;
          text-align: center;
        }
      }
    }
    &__faqs--title,
    &__faqs--subtitle {
      text-align: center;
    }
  }
}
</style>
